import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// import {
//   Dialog,
//   DialogContent,
//   DialogDescription,
//   DialogHeader,
//   DialogTitle,
//   DialogFooter
// } from "@/components/dialog-unstyled"; // or default UI dialog
import {
  Bot,
  FileSpreadsheet,
  Mail,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Send,
  Eye,
  Layers,
  ArrowRight
} from "lucide-react";
import { LanguageStore } from "@/Services/LanguageStore";
import { BridgeLanguage } from "@/Services/Language";
import { decodeWithBase64 } from "@/Services/encodedecode";

type EmailDeployment = {
  name: string;
  sheetsUrl: string;
  subject: string;
  body: string;
};

interface EmailSenderProps {
  onClose: () => void;
  onDeploy: (payload: EmailDeployment) => Promise<void> | void;
}

const EmailSender = ({ onClose, onDeploy }: EmailSenderProps) => {
  const defaultName = "Cosmitude Email tracker agent";
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isDeploying, setIsDeploying] = useState(false);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");

  const SHEETS_RE = /^https?:\/\/docs\.google\.com\/spreadsheets\/d\/[A-Za-z0-9-_]+/;
  const isValidSheets = useMemo(() => SHEETS_RE.test(sheetsUrl), [sheetsUrl]);
  const canSubmit = isValidSheets && subject.trim().length > 0 && body.trim().length > 0;

  const handleDeploy = async () => {
    if (!canSubmit) return;
    try {
      setIsDeploying(true);
      await onDeploy({ name: defaultName, sheetsUrl, subject, body });
    } finally {
      setIsDeploying(false);
    }
  };

  const LanguageString = (number: any) => {
    let LangData = LanguageStore.allLanguages;
    if (Object.keys(LangData).length === 0) {
      let localLang = localStorage.getItem('BridgeLanguageStrings');
      if (localLang) {
        LangData = JSON.parse(decodeWithBase64(localLang));
      }
    }
    if (LangData && LangData[number]) {
      return LangData[number];
    }
    else {
      return BridgeLanguage(number);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md transition-all duration-300 animate-in fade-in">
      <div className="relative w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden text-slate-800 dark:text-slate-100 transition-all">

        {/* Artistic Gradient Decorative Banner */}
        <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 p-6 sm:p-8 text-white overflow-hidden">
          {/* Subtle Ambient Background Orbs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-12 w-44 h-44 bg-blue-400/20 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex items-start justify-between">
            <div className="flex items-center space-x-3.5">
              <div className="p-3 bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 shadow-inner">
                <Mail className="h-7 w-7 text-white drop-shadow-sm" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 text-[10px] uppercase font-bold tracking-wider bg-white/20 text-white rounded-full backdrop-blur-sm border border-white/25">
                    Agent Configurator
                  </span>
                  <Sparkles className="h-3.5 w-3.5 text-amber-300 animate-pulse" />
                </div>
                <h2 className="text-2xl font-bold text-white tracking-tight mt-0.5">
                  {LanguageString("2067") || "Deploy Email Tracker Agent"}
                </h2>
                <p className="text-xs sm:text-sm text-blue-100/90 mt-1 max-w-lg leading-relaxed">
                  {LanguageString("2068") || "Configure your Google Sheets roster & automated status reminder loop."}
                </p>
              </div>
            </div>

            {/* Edit / Preview Switcher */}
            <div className="hidden sm:flex items-center p-1 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <button
                type="button"
                onClick={() => setActiveTab("edit")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === "edit"
                    ? "bg-white text-indigo-900 shadow-sm"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
              >
                <Layers className="h-3.5 w-3.5" />
                Form Setup
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("preview")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === "preview"
                    ? "bg-white text-indigo-900 shadow-sm"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
              >
                <Eye className="h-3.5 w-3.5" />
                Mail Preview
              </button>
            </div>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6">
          {activeTab === "edit" ? (
            <>
              {/* Agent Title Card */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider block">
                      {LanguageString("2059") || "Agent Name"}
                    </label>
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {defaultName}
                    </span>
                  </div>
                </div>
                <span className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-full border border-emerald-200 dark:border-emerald-800 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  Ready to Provision
                </span>
              </div>

              {/* Step 1: Google Sheets Integration */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sheetsUrl" className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                    <span>{LanguageString("2060") || "Google Sheets Roster URL"}</span>
                  </Label>
                  {sheetsUrl && (
                    <span className="text-xs flex items-center gap-1 font-medium">
                      {isValidSheets ? (
                        <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Valid Spreadsheet Link
                        </span>
                      ) : (
                        <span className="text-rose-500 flex items-center gap-1">
                          <AlertCircle className="h-3.5 w-3.5" /> Invalid Google Sheets URL
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="sheetsUrl"
                    placeholder="https://docs.google.com/spreadsheets/d/your-sheet-id/edit"
                    value={sheetsUrl}
                    onChange={(e) => setSheetsUrl(e.target.value)}
                    className={`pl-4 pr-10 py-3 rounded-xl border text-sm transition-all shadow-sm ${isValidSheets
                        ? "border-emerald-300 focus:ring-emerald-400 focus:border-emerald-500 bg-emerald-50/20 dark:bg-emerald-950/10"
                        : sheetsUrl
                          ? "border-rose-300 focus:ring-rose-400 focus:border-rose-500 bg-rose-50/20 dark:bg-rose-950/10"
                          : "border-slate-200 dark:border-slate-700 focus:ring-indigo-500 focus:border-indigo-500"
                      }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <FileSpreadsheet className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-normal pl-1">
                  {isValidSheets
                    ? LanguageString("2069") || "✓ Sheet link verified. Make sure sheet viewing permissions allow access."
                    : LanguageString("2070") || "Paste a valid Google Sheets URL (e.g. https://docs.google.com/spreadsheets/d/...)"}
                </p>
              </div>

              {/* Step 2: Subject Line */}
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <span>{LanguageString("2062") || "Main Email Subject"}</span>
                </Label>
                <Input
                  id="subject"
                  placeholder={LanguageString("2063") || "e.g., Weekly Project Status Update Needed"}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="rounded-xl border-slate-200 dark:border-slate-700 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-sm py-3"
                />
                {subject.trim() && (
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium pl-1">
                    Preview: “{subject}”
                  </p>
                )}
              </div>

              {/* Step 3: Mail Body Template */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="body" className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {LanguageString("2047") || "Email Body Message"}
                  </Label>
                  <span className="text-xs text-slate-400">Markdown & HTML supported</span>
                </div>
                <textarea
                  id="body"
                  className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800/80 p-4 text-sm min-h-[150px] focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all shadow-sm font-sans"
                  placeholder={LanguageString("2065") || "Hi Team,\n\nPlease reply to this email with your current project status update.\n\nThank you!"}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>
            </>
          ) : (
            /* Live Artistic Email Preview Tab */
            <div className="space-y-4 animate-in fade-in duration-200">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-slate-400 tracking-wider">
                  Live Recipient View
                </span>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-800">
                  Deliverability Shield Active
                </span>
              </div>

              {/* Mock Inbox Window */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 overflow-hidden shadow-sm">
                <div className="bg-slate-100 dark:bg-slate-800/80 px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-rose-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                    <span className="ml-2 font-mono text-[11px] text-slate-400">Inbox Preview</span>
                  </div>
                  <span>Cosmitude Email Tracker</span>
                </div>
                <div className="p-6 bg-white dark:bg-slate-900 space-y-4">
                  <div className="pb-3 border-b border-slate-100 dark:border-slate-800 space-y-1">
                    <div className="text-xs text-slate-400">
                      <span className="font-semibold text-slate-600 dark:text-slate-300">Subject:</span> {subject || "(No subject entered yet)"}
                    </div>
                    <div className="text-xs text-slate-400">
                      <span className="font-semibold text-slate-600 dark:text-slate-300">From:</span> Automated Tracker Agent
                    </div>
                  </div>
                  <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed min-h-[120px]">
                    {body || "(No message body entered yet)"}
                  </div>
                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-400 text-center">
                    This email was sent by <strong className="text-slate-600 dark:text-slate-300">Cosmitude Email Tracker</strong>.<br />
                    If you no longer wish to receive these emails, <span className="underline cursor-pointer text-indigo-500">click here to unsubscribe</span>.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-6 bg-slate-50/80 dark:bg-slate-900/80 border-t border-slate-200/80 dark:border-slate-800 flex items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <span className={`w-2.5 h-2.5 rounded-full ${canSubmit ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              {canSubmit ? "Ready to Launch Agent" : "Complete required fields"}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium text-sm px-5 py-2.5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeploy}
              disabled={isDeploying || !canSubmit}
              aria-busy={isDeploying}
              className="rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-semibold text-sm px-6 py-2.5 shadow-md shadow-indigo-500/20 hover:shadow-lg hover:shadow-indigo-500/30 active:scale-[0.98] transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isDeploying ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  <span>{LanguageString("2066") || "Deploying Agent..."}</span>
                </>
              ) : (
                <>
                  <span>{LanguageString("2013") || "Deploy Email Tracker"}</span>
                  <Send className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmailSender;